"use client";

import * as React from "react";

import { cn } from "../../lib/utils";
import {
	SelectCoreProvider,
	SelectCoreTrigger,
	SelectCoreContent,
	SelectCoreItem,
	SelectCoreGroup,
	SelectCoreSeparator,
	useSelectCoreContext,
	useControllableState,
	makeCollectItemLabels,
	type SelectCoreTriggerProps,
	type SelectCoreContentProps,
	type SelectCoreItemProps,
	type SelectedItem,
} from "./combobox-base";

/** Props for the {@link Combobox} root component. */
export type ComboboxProps = {
	children: React.ReactNode;
	/** Controlled selected value. When provided the component is fully controlled. */
	value?: string;
	/** Initial selected value for uncontrolled usage. */
	defaultValue?: string;
	/** Callback fired when the selected value changes. */
	onValueChange?: (value: string) => void;
	/** Callback fired when the popover open state changes (e.g. to save on close). */
	onOpenChange?: (open: boolean) => void;
};

/**
 * Composable single-value selector built on {@link Popover} and {@link Command}.
 *
 * A searchable alternative to {@link Select}: selecting an option sets the value
 * and closes the popover. Supports both controlled (`value` + `onValueChange`)
 * and uncontrolled (`defaultValue`) modes.
 */
function Combobox(props: ComboboxProps) {
	const { children, value, defaultValue, onValueChange, onOpenChange } = props;
	// Controlled when the `value` prop is present, even if it is `undefined`
	// (e.g. a controlled-but-initially-empty field). Keying off presence rather
	// than the value keeps controlled-ness stable across clears.
	const isControlled = "value" in props;

	const [open, setOpenInternal] = React.useState(false);
	const setOpen = React.useCallback(
		(nextOpen: boolean) => {
			setOpenInternal(nextOpen);
			onOpenChange?.(nextOpen);
		},
		[onOpenChange],
	);

	const [selectedValue, setSelectedValue] = useControllableState(
		isControlled,
		value,
		defaultValue,
	);

	function toggle(nextValue: string) {
		setSelectedValue(nextValue);
		onValueChange?.(nextValue);
		setOpen(false);
	}

	return (
		<SelectCoreProvider
			open={open}
			setOpen={setOpen}
			multiple={false}
			selectedValues={selectedValue === undefined ? [] : [selectedValue]}
			isSelected={(v) => v === selectedValue}
			toggle={toggle}
			collectItemLabels={collectComboboxItemLabels}
		>
			{children}
		</SelectCoreProvider>
	);
}
Combobox.displayName = "Combobox";

/** Props for the {@link ComboboxTrigger} component. */
export type ComboboxTriggerProps = SelectCoreTriggerProps;

/** Trigger button that opens the combobox popover. Renders as a combobox. */
const ComboboxTrigger = SelectCoreTrigger;

/** Props for the {@link ComboboxValue} component. */
export type ComboboxValueProps = {
	/** Text shown when nothing is selected. */
	placeholder?: string;
	/**
	 * Render a custom display for the selected value. Takes precedence over the
	 * default label and owns rendering for all states, including when nothing is
	 * selected (`null`).
	 */
	format?: (selected: SelectedItem | null) => React.ReactNode;
} & Omit<React.ComponentPropsWithoutRef<"span">, "children">;

/** Renders the currently selected value's label, or a placeholder when unset. */
function ComboboxValue({
	placeholder,
	className,
	format,
	...props
}: ComboboxValueProps) {
	const { selectedValues, items } = useSelectCoreContext();
	const value = selectedValues[0];
	const selected: SelectedItem | null =
		value === undefined ? null : { value, label: items.get(value) ?? value };

	if (format) {
		return (
			<span
				{...props}
				className={cn("min-w-0 overflow-hidden text-left", className)}
			>
				{format(selected)}
			</span>
		);
	}

	if (selected === null) {
		return (
			<span
				{...props}
				className={cn(
					"min-w-0 overflow-hidden font-normal text-neutral-500 dark:text-neutral-400",
					className,
				)}
			>
				{placeholder}
			</span>
		);
	}

	return (
		<span
			{...props}
			className={cn("min-w-0 overflow-hidden text-left", className)}
		>
			{selected.label}
		</span>
	);
}
ComboboxValue.displayName = "ComboboxValue";

/** Props for the {@link ComboboxContent} component. */
export type ComboboxContentProps = SelectCoreContentProps;

/** Popover content containing a searchable list of options. */
const ComboboxContent = SelectCoreContent;

/** Props for the {@link ComboboxItem} component. */
export type ComboboxItemProps = {
	/** The string value that identifies this option. */
	value: string;
	/** Override the label shown in the trigger. Falls back to `children`. */
	label?: React.ReactNode;
	/** Muted subtext rendered beneath the option label. Never shown in the trigger. */
	description?: React.ReactNode;
} & Omit<SelectCoreItemProps, "value" | "label">;

/** A single selectable option within {@link ComboboxContent}. */
function ComboboxItem({ label, ...props }: ComboboxItemProps) {
	return <SelectCoreItem label={label} {...props} />;
}
ComboboxItem.displayName = "ComboboxItem";

const collectComboboxItemLabels = makeCollectItemLabels(
	ComboboxItem,
	(itemProps) => itemProps.label as React.ReactNode,
);

/** Visually groups related {@link ComboboxItem}s under an optional heading. */
const ComboboxGroup = SelectCoreGroup;

/** Horizontal rule between groups of {@link ComboboxItem}s. */
const ComboboxSeparator = SelectCoreSeparator;

/** Props for the {@link ComboboxClear} component. */
export type ComboboxClearProps = {
	/** Called when the clear action is triggered (e.g. to reset the selected value). */
	onClear: () => void;
	/** Label for the clear action. Defaults to "Clear selection". */
	children?: React.ReactNode;
	className?: string;
	/** Whether clearing also closes the popover. Defaults to `true`. */
	closeOnClear?: boolean;
};

/**
 * A footer action (rendered via {@link ComboboxContent}'s `footer` slot) that
 * clears the current selection and closes the popover. Renders nothing while
 * there is no selection.
 */
function ComboboxClear({
	onClear,
	children,
	className,
	closeOnClear = true,
}: ComboboxClearProps) {
	const { selectedValues, setOpen } = useSelectCoreContext();

	if (selectedValues.length === 0) {
		return null;
	}

	return (
		<button
			type="button"
			onClick={() => {
				onClear();
				if (closeOnClear) {
					setOpen(false);
				}
			}}
			className={cn(
				"w-full border-t border-neutral-200 px-3 py-2 text-left text-sm font-medium text-primary hover:bg-neutral-50",
				className,
			)}
		>
			{children ?? "Clear selection"}
		</button>
	);
}
ComboboxClear.displayName = "ComboboxClear";

export {
	Combobox,
	ComboboxTrigger,
	ComboboxValue,
	ComboboxContent,
	ComboboxItem,
	ComboboxGroup,
	ComboboxSeparator,
	ComboboxClear,
};
